import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Container } from 'src/components/Container';
import { EmptyState } from 'src/components/EmptyState/EmptyState';
import { H1, Text } from 'src/components/Text';
import { messages } from 'src/messages';
import { GitHubAppManifestForm } from './GithubForm';
import { GithubRepositoryInfo, OrgGitHubApp } from 'src/orchd-client';
import { orgsApi } from 'src/api_services/orgs/service';
import { AppRepos } from './AppRepos';
import { css } from '@emotion/core';
import { transparentize } from 'polished';
import { Theme } from 'src/design-system/theme';
import { SquarePill } from 'src/components/SquarePill/SquarePill';
import { Icon } from 'src/components/Icon';
import { Loading } from 'src/components/Loading';
import { useModal } from 'src/utils/modal/useModal';
import { DeployToWebsite, GithubDeployTargetKind } from './DeployToWebsite';
import { useSelector } from 'react-redux';
import { sessionSelectors } from 'src/store/session/selectors';
import { BreadcrumbsItem } from 'src/containers/Breadcrumbs';

export const ProjectsDashboard = () => {
  const [app, setApp] = useState<OrgGitHubApp | null>(null);
  const [appLoading, setAppLoading] = useState(true);
  const [repos, setRepos] = useState<GithubRepositoryInfo[]>([]);
  const [reposError, setReposError] = useState<string | null>(null);
  // starts as true so the single loader below does not flash page content in
  // the frame between the app resolving and the repos fetch kicking off
  const [reposLoading, setReposLoading] = useState(true);

  const member = useSelector(sessionSelectors.getMemberOrUndefined);

  const openNewWebsiteModal = useModal(DeployToWebsite);
  const openExistingWebsiteModal = useModal(DeployToWebsite);
  const intl = useIntl();
  const showConnect = false;

  const getApp = useCallback(() => {
    setAppLoading(true);
    orgsApi
      .getOrgGithubApp({
        orgId: member.orgId,
      })
      .then((data) => {
        setApp(data.data);
      })
      .catch((e) => {
        if (e.status !== 404) {
          console.error('Failed to fetch GitHub app:', e);
        }
        setApp(null);
      })
      .finally(() => setAppLoading(false));
  }, [member.orgId]);

  const getRepos = useCallback(() => {
    setReposLoading(true);
    setReposError(null);
    orgsApi
      .getOrgGithubAppInfo({
        orgId: member.orgId,
      })
      .then((data) => setRepos(data.data))
      .catch((e) => {
        console.error('Failed to fetch repositories:', e);
        setReposError('Failed to connect to GitHub. Please try again.');
      })
      .finally(() => setReposLoading(false));
  }, [member.orgId]);

  useEffect(() => {
    getApp();
  }, [getApp]);

  useEffect(() => {
    if (app) {
      getRepos();
    }
  }, [app, getRepos]);

  // one loader for both the connection fetch and the repository list; this
  // single component instance stays mounted for the whole wait so the
  // spinner never restarts between the two fetches
  if (appLoading || (app !== null && reposLoading)) {
    return <Loading absoluteCenter adjustForSpine />;
  }

  if (!app) {
    return (
      <Container>
        <Box j="space-between" a="center" wrap="nowrap" mb="2xl" h="48px">
          <Box d="column" mr="xl" j="center">
            <H1 size="3xl">
              <FormattedMessage id="github_connection" defaultMessage="{git} connection" values={{ git: 'GitHub' }} />
            </H1>
          </Box>
        </Box>
        <EmptyState
          icon="projects_empty_state"
          title={
            <FormattedMessage
              id="git.projects.empty_state_title_not_connected"
              defaultMessage="No GitHub connection yet"
            />
          }
          message={
            <FormattedMessage
              id="git.projects.empty_state_guidance"
              defaultMessage="Connect to GitHub to automatically deploy projects to new or existing websites within this account."
            />
          }
          buttonComponent={<GitHubAppManifestForm member={member} onClose={getApp} />}
        />
      </Container>
    );
  }

  return (
    <Container>
      <BreadcrumbsItem id="home-breadcrumb" label={intl.formatMessage(messages.home)} url="/" />

      <BreadcrumbsItem
        label={<FormattedMessage {...messages.connect_github} values={{ github: 'GitHub' }} />}
        url={`/github`}
      />
      <Box j="space-between" a="center" wrap="nowrap" mb="2xl" h="48px">
        <Box d="column" mr="xl" j="center">
          <H1 size="3xl">
            <FormattedMessage id="github_connection" defaultMessage="{git} connection" values={{ git: 'GitHub' }} />
          </H1>
        </Box>

        {showConnect && (
          <Box j="flex-end" a="center">
            <Button marginLeft="3xl" onClick={() => {}} icon="github">
              <FormattedMessage {...messages.connect_github} />
            </Button>
          </Box>
        )}
      </Box>

      <Box
        css={(theme: Theme) =>
          css`
            border: 1px solid ${theme.colors.success};
            width: 100%;
            padding: 24px;
            border-radius: 2px;
            position: relative;
            margin-bottom: 24px;
            background-color: ${theme.colors.light};
          `
        }
      >
        <Box
          css={css`
            position: absolute;
            top: -12px;
            left: 16px;
          `}
        >
          <SquarePill
            size="sm"
            color="success"
            text={<FormattedMessage id="github.projects.connected_pill" defaultMessage="Connected" />}
          />
        </Box>
        <Box
          css={(theme: Theme) =>
            css`
              background-color: ${transparentize(0.8)(theme.colors.success)};
              padding: 24px;
              flex-direction: row;
              align-items: center;
              border-radius: 8px;
              display: flex;
            `
          }
        >
          <Icon name="github" size="2xl" marginRight="xl" />
          <Box d="row" j="space-between" a="center" w="auto" grow={1}>
            <Box d="column" w="auto">
              <Text size="sm" color="grey" marginBottom="xs">
                Owner
              </Text>
              <Box d="row" w="auto" a="center" mb={{ xs: 'sm', md: undefined }}>
                <Text>{app.owner.login}</Text>
              </Box>
            </Box>
            <Button
              variant="tertiary"
              size="small"
              onClick={async () => {
                try {
                  await orgsApi.deleteOrgGithubApp({ orgId: member.orgId });
                  setApp(null);
                } catch (e) {
                  console.error('Failed to disconnect GitHub app:', e);
                }
              }}
            >
              <FormattedMessage id="git_deploy.disconnect_account_cta" defaultMessage="Disconnect account" />
            </Button>
          </Box>
        </Box>
      </Box>

      {reposError ? (
        <EmptyState
          icon="error"
          title={
            <FormattedMessage id="git.projects.connection_error_title" defaultMessage="Unable to connect to GitHub" />
          }
          message={
            <FormattedMessage
              id="git.projects.connection_error_message"
              defaultMessage="We're having trouble connecting to your GitHub account at this time. This could be due to network issues or your GitHub connection expiring."
            />
          }
          buttonComponent={<GitHubAppManifestForm member={member} onClose={getRepos} />}
        />
      ) : (
        <AppRepos
          repos={repos}
          onDeployToNewWebsite={(repo) => {
            openNewWebsiteModal({
              name: repo.name,
              full_name: repo.full_name,
              owner: repo.owner,
              id: repo.id,
              _private: repo._private,
              clone_url: repo.clone_url,
              default_branch: repo.default_branch,
              targetKind: GithubDeployTargetKind.newWebsite,
            });
          }}
          onDeployToExistingWebsite={(repo) => {
            openExistingWebsiteModal({
              name: repo.name,
              full_name: repo.full_name,
              owner: repo.owner,
              id: repo.id,
              _private: repo._private,
              clone_url: repo.clone_url,
              default_branch: repo.default_branch,
              targetKind: GithubDeployTargetKind.existingWebsite,
            });
          }}
        />
      )}
    </Container>
  );
};
